import { Moon, Sun } from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { cn } from '../lib/cn';

/**
 * ThemeToggle — single-button toggle between light and dark.
 *
 * Reads the resolved theme (not the user mode) so the icon reflects what is
 * currently shown. Clicking sets the user preference to the opposite of what
 * is currently shown, which means: a user on `system` who toggles ends up on
 * an explicit choice. To go back to following the OS, use `useTheme().setMode('system')`
 * directly (a more elaborate menu, not this primitive).
 *
 * The button uses an `aria-label` written in voseo, per
 * `voice-and-vocabulary.md` §2. The icon is decorative and `aria-hidden`.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? 'Cambiar al tema claro' : 'Cambiar al tema oscuro'}
      title={isDark ? 'Tema claro' : 'Tema oscuro'}
      className={cn(
        'focus-ring inline-flex h-9 w-9 items-center justify-center rounded text-ink-muted transition-colors hover:bg-paper-tinted hover:text-ink',
        className,
      )}
    >
      {isDark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
    </button>
  );
}
